import { throwIfAborted } from '@admin-panel/utils';
import axios, { AxiosResponse } from 'axios';
import type { IAsset } from '@admin-panel/types';
import { axiosInstance } from './clients/axios';
import { GATEWAY_API_URL } from '@admin-panel/config/env';
import {
  normalizeAssetUploadError,
  prepareAssetUploadFile,
  preparePackingVideoFile,
} from './assetuploadpreparation';
import { isDirectMp4 } from './mp4Compatibility';
import { PACKING_UPLOAD_MAX_BYTES, PACKING_UPLOAD_STALL_MS } from './packingUploadLimits';
import { uploadDeadline } from './uploadDeadline';
import { recordPackingTransferFailure, uncertainPackingTransfer } from './packingUploadFailure';
import { packingUploadDiagnostics } from './packingUploadDiagnostics';
import { assetDeviceCategory, type AssetUploadProvenance } from './assetUploadProvenance';
export { assetDeviceCategory } from './assetUploadProvenance';
export type { AssetUploadProvenance } from './assetUploadProvenance';

type UploadableAssetFile = File & {
  id?: string;
  url?: string;
  asset?: IAsset;
  uploadProvenance?: AssetUploadProvenance;
};

interface UploadAssetParams {
  file: UploadableAssetFile;
  onError?: (error: Error & { retryable?: boolean }) => void;
  onSuccess?: (file: UploadableAssetFile) => void;
  source?: string;
  usage: string;
  transfer?: AssetTransferOptions;
  packingVideo?: boolean;
}

export interface AssetUploadCheckpoint {
  path: string;
  uploaded: boolean;
  sourceId?: string;
  storageProvider?: 'AWS_S3';
  uploadToken?: string;
}

type UploadInstructions = AssetUploadCheckpoint & {
  url?: string;
  uploadMethod?: 'PUT' | 'POST';
  uploadHeaders?: Record<string, string>;
};

export interface AssetTransferOptions extends Partial<AssetUploadCheckpoint> {
  packingPackageId?: number;
  provenance?: AssetUploadProvenance;
  onCheckpoint?: (checkpoint: AssetUploadCheckpoint) => void;
  stallTimeoutMs?: number;
  signal?: AbortSignal;
  timeoutMs?: number;
  onProgress?: (percent: number) => void;
  onTransferProgress?: (progress: { loaded: number; total?: number; elapsedMs: number }) => void;
  onTransferComplete?: (sample: { bytes: number; elapsedMs: number }) => void;
  onStage?: (
    stage: 'initializing' | 'transferring' | 'confirming' | 'checking' | 'finishing',
  ) => void;
}

export const PACKING_VIDEO_UPLOAD_TIMEOUT_MS = 30 * 60 * 1000;

// Older Platform MediaType resolves these non-video MIME types before consulting a suffix.
// Keep this fallback only for responses without authoritative uploadMethod instructions.
const legacyNonVideoMimeTypes = new Set([
  'application/octet-stream',
  'application/zip',
  'text/csv',
  'application/pdf',
  'application/dmg',
  'application/docx',
  'application/wav',
  'application/flac',
  'text/html',
  'application/json',
  'application/xls',
  'application/vnd.ms-excel',
  'image/jpg',
  'image/gif',
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/svg+xml',
  'audio/mp3',
]);

// Diagnostic context only, never package authorization or proof of attachment.
// An existing checkpoint without context must remain historical/unknown.
const packingSourceId = (options?: AssetTransferOptions) => {
  if (options?.sourceId) return options.sourceId;
  const id = options?.packingPackageId;
  return !options?.path && typeof id === 'number' && Number.isSafeInteger(id) && id > 0
    ? `fulfillment/package/${id}`
    : undefined;
};

export class AssetUploaderClient {
  /** Bounded recorder upload; keep identity across finish/attachment retries. */
  async uploadRecordedVideo(
    file: File,
    options: AssetTransferOptions &
      Partial<AssetUploadCheckpoint> & {
        onCheckpoint?: (checkpoint: AssetUploadCheckpoint) => void;
      } = {},
  ): Promise<IAsset> {
    throwIfAborted(options.signal);
    file = preparePackingVideoFile(file);
    // Recorder/browser hints and chunk size limits are not a hard file-size guarantee.
    if (!(options.path && options.uploaded) && file.size > PACKING_UPLOAD_MAX_BYTES)
      throw new Error(
        'Packing videos must be 200 MB or smaller. Download this clip and select a smaller video.',
      );
    const diagnostics = packingUploadDiagnostics({
      packageId: options.packingPackageId,
      bytes: file.size,
      mimeType: file.type,
      ...options.provenance,
      signal: options.signal,
    });
    try {
      let path = options.path;
      if (path) diagnostics.checkpoint(path);
      let uploadToken = options.uploadToken;
      let provider = options.storageProvider;
      const sourceId = packingSourceId(options);
      // Negotiate conversion for other videos; a processing-disabled server still returns Stream.
      // Existing identities always resume their original provider, regardless of rollout flags.
      const uploadProtocol = path
        ? 'processed-video-v1'
        : (await isDirectMp4(file, options.signal))
          ? 'direct-mp4-v1'
          : 'processed-video-v1';
      if (!path || (provider === 'AWS_S3' && !options.uploaded)) {
        options.onStage?.('initializing');
        diagnostics.phase('INITIALIZATION');
        const initialized: UploadInstructions = await this.initFileUpload(
          file,
          'VIDEO',
          'ASSET',
          options.signal,
          {
            ...(uploadProtocol ? { uploadProtocol } : {}),
            pathPrefix: 'fulfillment/packing',
            ...(sourceId ? { sourceId } : {}),
            contentLength: file.size,
            ...options.provenance,
            ...(path ? { path, uploadToken } : {}),
          },
        );
        if (
          !initialized?.path ||
          (!initialized.uploaded && !initialized.url) ||
          (path && path !== initialized.path)
        )
          throw new Error('Video upload could not be initialized');
        path = initialized.path;
        diagnostics.checkpoint(path);
        uploadToken = initialized.uploadToken ?? uploadToken;
        provider = initialized.storageProvider ?? provider;
        const checkpoint = {
          path,
          uploaded: Boolean(initialized.uploaded),
          ...(sourceId ? { sourceId } : {}),
          ...(provider ? { storageProvider: provider, uploadToken } : {}),
        };
        options.onCheckpoint?.(checkpoint);
        if (!initialized.uploaded) {
          options.onStage?.('transferring');
          diagnostics.phase('TRANSFER');
          try {
            await this.uploadFileToCloudflare(
              initialized.url!,
              file,
              {
                ...options,
                path,
                timeoutMs: options.timeoutMs ?? PACKING_VIDEO_UPLOAD_TIMEOUT_MS,
                stallTimeoutMs: options.stallTimeoutMs ?? PACKING_UPLOAD_STALL_MS,
              },
              initialized,
              diagnostics,
            );
          } catch (error) {
            // A lost response or create-only conflict may follow a successful PUT.
            // Check once through existing exact-identity init/HEAD; never retransmit here.
            if (
              options.signal?.aborted ||
              provider !== 'AWS_S3' ||
              !uncertainPackingTransfer(error)
            )
              throw error;
            options.onStage?.('checking');
            let verified: UploadInstructions | undefined;
            try {
              verified = await this.initFileUpload(file, 'VIDEO', 'ASSET', options.signal, {
                uploadProtocol: 'processed-video-v1',
                pathPrefix: 'fulfillment/packing',
                ...(sourceId ? { sourceId } : {}),
                contentLength: file.size,
                path,
                uploadToken,
              });
            } catch {
              diagnostics.verification('CHECK_FAILED');
              // Preserve the original transfer error and retained checkpoint for manual recovery.
            }
            throwIfAborted(options.signal);
            if (verified?.uploaded !== true || verified.path !== path) {
              if (verified) diagnostics.verification('NOT_CONFIRMED');
              throw error;
            }
          }
        }
        throwIfAborted(options.signal);
        options.onCheckpoint?.({ ...checkpoint, uploaded: true });
      } else if (!options.uploaded) {
        // An interrupted direct POST is not proof of upload. Do not finish an empty asset.
        throw new Error('Video upload was interrupted. Retry uploading the retained clip.');
      }
      throwIfAborted(options.signal);
      options.onStage?.('finishing');
      diagnostics.phase('FINALIZATION');
      const asset = await this.finishFileUpload(path!, options.signal, uploadToken);
      throwIfAborted(options.signal);
      if (
        !(Number(asset?.id) > 0) ||
        asset?.uploaded !== true ||
        (!asset?.url && asset?.storageProvider !== 'AWS_S3')
      )
        throw new Error('Video upload completion was not confirmed');
      return asset;
    } catch (failure) {
      diagnostics.report(failure);
      throw failure;
    }
  }

  async uploadFile({
    file,
    onError,
    onSuccess,
    usage,
    source,
    transfer,
    packingVideo,
  }: UploadAssetParams) {
    let delegatedPackingVideo = false;
    const preparationDiagnostic =
      packingVideo && usage === 'VIDEO'
        ? packingUploadDiagnostics({
            packageId: transfer?.packingPackageId,
            bytes: file.size,
            mimeType: file.type,
            uploadMethod: 'FILE_UPLOAD',
            deviceCategory: file.uploadProvenance?.deviceCategory ?? assetDeviceCategory(),
            signal: transfer?.signal,
          })
        : undefined;
    try {
      // Freeze before preparation/I/O; reuse the same metadata if this file is retried.
      if (usage === 'VIDEO')
        file.uploadProvenance ??= {
          uploadMethod: 'FILE_UPLOAD',
          deviceCategory: assetDeviceCategory(),
        };
      if (!source) source = 'ASSET';
      const uploadFile = (await prepareAssetUploadFile(file)) as UploadableAssetFile;

      if (packingVideo && usage === 'VIDEO') {
        delegatedPackingVideo = true;
        const asset = await this.uploadRecordedVideo(uploadFile, {
          ...transfer,
          provenance: file.uploadProvenance,
        });
        uploadFile.url = asset.url;
        uploadFile.id = String(asset.id);
        uploadFile.asset = asset;
        onSuccess?.(uploadFile);
        return;
      }

      throwIfAborted(transfer?.signal);
      const sourceId = packingVideo ? packingSourceId(transfer) : undefined;
      const uploadRequestInfo = await this.initFileUpload(
        uploadFile,
        usage,
        source,
        transfer?.signal,
        {
          ...file.uploadProvenance,
          ...(sourceId ? { sourceId } : {}),
        },
      );
      await this.uploadFileToCloudflare(
        uploadRequestInfo.url,
        uploadFile,
        transfer,
        uploadRequestInfo,
      );
      throwIfAborted(transfer?.signal);
      const uploadedAssetInfo = await this.finishFileUpload(
        uploadRequestInfo.path,
        transfer?.signal,
      );
      throwIfAborted(transfer?.signal);
      uploadFile.url = uploadedAssetInfo.url;
      uploadFile.id = uploadedAssetInfo.id;
      onSuccess?.(uploadFile);
    } catch (error: unknown) {
      if (!delegatedPackingVideo) preparationDiagnostic?.report(error);
      onError?.(normalizeAssetUploadError(error));
    }
  }

  private async initFileUpload(
    file: UploadableAssetFile,
    usage: string,
    source?: string,
    signal?: AbortSignal,
    contract?: Record<string, unknown>,
  ) {
    const payload = {
      usage: usage,
      type: file.type,
      name: file.name,
      source: source ? source : 'ASSET',
      ...contract,
    };
    const response: AxiosResponse = await axiosInstance.post(
      `${GATEWAY_API_URL}/asset/upload/init`,
      payload,
      signal ? { signal, timeout: 30000 } : {},
    );
    return response.data?.data;
  }

  private async uploadFileToCloudflare(
    url: string,
    file: UploadableAssetFile,
    transfer?: AssetTransferOptions,
    instructions?: UploadInstructions,
    diagnostics?: ReturnType<typeof packingUploadDiagnostics>,
  ) {
    const axios_instance = axios.create();
    const started = performance.now();
    let loaded = 0;
    const deadline = uploadDeadline(transfer?.signal, transfer?.stallTimeoutMs);
    const onUploadProgress =
      transfer?.onProgress ||
      transfer?.onTransferProgress ||
      transfer?.onStage ||
      diagnostics ||
      transfer?.stallTimeoutMs
        ? (event: { loaded: number; total?: number }) => {
            loaded = Math.max(loaded, event.loaded);
            diagnostics?.progress(loaded);
            deadline.progress(event.loaded);
            transfer?.onTransferProgress?.({
              loaded: event.loaded,
              total: event.total,
              elapsedMs: performance.now() - started,
            });
            if (event.total) {
              transfer?.onProgress?.(Math.min(100, Math.floor((event.loaded / event.total) * 100)));
              if (event.loaded >= event.total) transfer?.onStage?.('confirming');
            }
          }
        : undefined;

    let response: AxiosResponse | undefined = undefined;

    try {
      if (
        instructions?.uploadMethod != null &&
        instructions.uploadMethod !== 'PUT' &&
        instructions.uploadMethod !== 'POST'
      )
        throw new Error('Unsupported upload transport');
      if (
        instructions?.storageProvider === 'AWS_S3' &&
        (instructions.uploadMethod !== 'PUT' ||
          !instructions.uploadToken ||
          !instructions.uploadHeaders?.['Content-Type'] ||
          instructions.uploadHeaders?.['If-None-Match'] !== '*')
      )
        throw new Error('Invalid AWS video upload instructions');

      // Older servers omit instructions. Match their media routing rather than business usage.
      const mime = file.type.toLowerCase();
      const legacyVideo =
        /^video\/(mp4|mkv|webm|mov|flv|avi|mpeg4)$/.test(mime) ||
        (!legacyNonVideoMimeTypes.has(mime) &&
          /\.(mp4|mov|webm|mkv|flv|avi|m4v)$/i.test(file.name));
      if (instructions?.uploadMethod ? instructions.uploadMethod === 'POST' : legacyVideo) {
        const formData = new FormData();
        formData.append('file', file);

        response = await axios_instance.post(url, formData, {
          signal: deadline.signal,
          ...(transfer?.timeoutMs ? { timeout: transfer.timeoutMs } : {}),
          onUploadProgress,
          headers: {
            'Content-Type': 'multipart/form-data',
            ...instructions?.uploadHeaders,
          },
        });
      } else {
        response = await axios_instance.put(url, file, {
          signal: deadline.signal,
          ...(transfer?.timeoutMs ? { timeout: transfer.timeoutMs } : {}),
          onUploadProgress,
          headers: {
            'Content-Type': file.type,
            ...instructions?.uploadHeaders,
          },
        });
      }
      throwIfAborted(transfer?.signal);
      transfer?.onTransferComplete?.({ bytes: file.size, elapsedMs: performance.now() - started });
      return response;
    } catch (error) {
      const failure = deadline.failure(error);
      if (transfer?.path && !transfer.signal?.aborted)
        recordPackingTransferFailure(failure, {
          packageId: transfer.packingPackageId,
          path: transfer.path,
          bytes: file.size,
          loaded,
          elapsedMs: Math.round(performance.now() - started),
        });
      throw failure;
    } finally {
      deadline.dispose();
    }
  }

  private async finishFileUpload(path: string, signal?: AbortSignal, uploadToken?: string) {
    const payload = { path: path, ...(uploadToken ? { uploadToken } : {}) };
    const response: AxiosResponse = await axiosInstance.post(
      `${GATEWAY_API_URL}/asset/upload/finish`,
      payload,
      signal ? { signal, timeout: 30000 } : {},
    );
    return response.data?.data;
  }
}
